/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import SimpleTable, {
    SimpleTableHeader,
} from 'common/components/table/SimpleTable';
import { hasRole, isContractorAccount } from 'common/services/AuthService';
import Drawer from 'common/components-v2/modal/Drawer';
import EditIcon from 'images/svg/edit-pen.svg';
import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import MotiliButtonContainer from 'common/components-v2/buttons/MotiliButtonContainer';
import styled from 'styled-components';
import Config from 'Config';
import styles from './AdminWorkOrderRequirementsChecklistViewModal.module.scss';
import { calculateProgress } from '../../services/AdminChecklistService';
import { generateRandomUUID } from '../../../common/utils/Utils';
import RadioGroup from '../../../common/components/form/RadioGroup';

const AdminWorkOrderRequirementsChecklistViewModal = ({
    checklist,
    handleCloseOnClick,
    handleSaveChecklistOnClick,
    workOrderStatus,
    workOrder,
    accountId,
    isOpen,
}) => {
    const [editing, setEditing] = useState(false);
    const [sections, setSections] = useState(
        checklist?.requirement?.sections ?? []
    );
    const [progress, setProgress] = useState();
    const [isSaving, setIsSaving] = useState(false);
    useEffect(() => {
        if (checklist?.requirement?.sections) {
            setSections(checklist?.requirement?.sections);
            setProgress(calculateProgress(checklist?.requirement?.sections));
        } else {
            setProgress('--');
        }
    }, [checklist]);

    useEffect(() => {
        setProgress(calculateProgress(sections));
    }, [sections]);

    useEffect(() => {
        if (isOpen) {
            setIsSaving(false);
            setEditing(false);
            setSections(checklist?.requirement?.sections ?? []);
        }
    }, [isOpen]);

    const itemOnClick = (checkBox, sectionPosition, itemPosition) => {
        setSections(
            sections.map(s =>
                s.sequencePosition === sectionPosition
                    ? {
                          ...s,
                          items: s.items.map(i => {
                              if (i.sequencePosition !== itemPosition) return i;

                              if (checkBox === 'complete') {
                                  return {
                                      ...i,
                                      complete: true,
                                      notApplicable: false,
                                  };
                              }

                              if (checkBox === 'notApplicable') {
                                  return {
                                      ...i,
                                      notApplicable: true,
                                      complete: false,
                                  };
                              }

                              return i;
                          }),
                      }
                    : s
            )
        );
    };
    return (
        <Drawer
            drawerStyle={{
                width: '740px',
                overflowY: 'hidden',
            }}
            drawerContainerStyle={{
                overflowY: 'auto',
                flex: '0 1000 auto',
            }}
            onClose={handleCloseOnClick}
            title={
                <span style={{ color: Config.color.darkCharcoal }}>
                    {checklist?.requirement?.name}
                </span>
            }
            subTitle={
                <div className={styles.percentComplete}>
                    <span
                        style={{
                            fontWeight: 600,
                            color: progress === 100 ? Config.color.green : null,
                        }}
                    >
                        {progress}% Complete
                    </span>
                    {_editingAvailable(
                        checklist,
                        workOrderStatus,
                        workOrder,
                        accountId
                    ) &&
                        !editing && (
                            <img
                                className={styles.editButton}
                                src={EditIcon}
                                title='Edit'
                                onClick={() => setEditing(true)}
                            />
                        )}
                </div>
            }
            open={isOpen}
            showClose={!isSaving}
            closeByDocument={!isSaving}
            inheritFooterStyles={false}
            footer={
                <RenderFooter
                    editing={editing}
                    handleClose={handleCloseOnClick}
                    handleSave={() => {
                        setIsSaving(true);
                        handleSaveChecklistOnClick(checklist, sections);
                    }}
                    isSaving={isSaving}
                />
            }
        >
            {checklist?.requirement && (
                <div>
                    {sections.map(section => (
                        <div key={section.sequencePosition}>
                            <ChecklistSection
                                section={section}
                                editing={editing}
                                itemOnClick={itemOnClick}
                            />
                        </div>
                    ))}
                </div>
            )}
        </Drawer>
    );
};

AdminWorkOrderRequirementsChecklistViewModal.propTypes = {
    checklist: PropTypes.shape({
        id: PropTypes.number.isRequired,
        requirement: PropTypes.shape({
            name: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
            sections: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string,
                    sequencePosition: PropTypes.number,
                    items: PropTypes.arrayOf(
                        PropTypes.shape({
                            description: PropTypes.string,
                            sequencePosition: PropTypes.number,
                        })
                    ),
                })
            ),
        }),
    }),
    handleCloseOnClick: PropTypes.func.isRequired,
    handleSaveChecklistOnClick: PropTypes.func.isRequired,
    workOrderStatus: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
};
const RenderFooter = ({ editing, handleClose, handleSave, isSaving }) => {
    const bottonSize = { width: '98px', height: '44px' };
    if (editing)
        return (
            <MotiliButtonContainer styles={{ paddingBottom: '90px' }}>
                <MotiliButton
                    text='Cancel'
                    onClick={handleClose}
                    disabled={isSaving}
                    moStyle='normal'
                    style={bottonSize}
                />
                <MotiliButton
                    text='Save'
                    onClick={handleSave}
                    busy={isSaving}
                    style={bottonSize}
                />
            </MotiliButtonContainer>
        );

    return (
        <MotiliButtonContainer styles={{ paddingBottom: '90px' }}>
            <MotiliButton
                text='Close'
                onClick={handleClose}
                style={bottonSize}
            />
        </MotiliButtonContainer>
    );
};

RenderFooter.propTypes = {
    editing: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleSave: PropTypes.func.isRequired,
    isSaving: PropTypes.bool.isRequired,
};

const ChecklistSection = ({ section, editing, itemOnClick }) => (
    <div className={styles.checklistSectionContainer}>
        <SimpleTable
            header={
                <SimpleTableHeader>
                    <th
                        style={{
                            width: '50%',
                            verticalAlign: 'top',
                            color: Config.color.black,
                        }}
                    >
                        {section.name.toUpperCase()}
                    </th>
                    <th
                        style={{
                            width: '120px',
                            textAlign: 'center',
                            verticalAlign: 'top',
                            color: Config.color.black,
                        }}
                    >
                        Completed
                    </th>
                    <th
                        style={{
                            width: '120px',
                            textAlign: 'center',
                            verticalAlign: 'top',
                            color: Config.color.black,
                        }}
                    >
                        N/A
                    </th>
                </SimpleTableHeader>
            }
        >
            {section.items.map(item => (
                <tr
                    key={`${section.sequencePosition} - ${item.sequencePosition}`}
                >
                    <ChecklistItem
                        item={item}
                        section={section}
                        editing={editing}
                        itemOnClick={itemOnClick}
                    />
                </tr>
            ))}
        </SimpleTable>
    </div>
);

ChecklistSection.propTypes = {
    section: PropTypes.shape({
        name: PropTypes.string,
        sequencePosition: PropTypes.number,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                description: PropTypes.string,
                sequencePosition: PropTypes.number,
            })
        ),
    }),
    editing: PropTypes.bool.isRequired,
    itemOnClick: PropTypes.func.isRequired,
};

const ChecklistItem = ({ item, section, editing, itemOnClick }) => {
    return (
        <>
            <td style={{ color: Config.color.darkCharcoal }}>
                {item.description}
            </td>
            <CenteredRadioCell>
                <RadioWrapper>
                    <StyledRadioGroup
                        name={`complete-${section.sequencePosition}-${item.sequencePosition}`}
                        options={[{ id: true, display: '' }]}
                        value={item.complete}
                        disabled={!editing}
                        onClick={() => {
                            itemOnClick(
                                'complete',
                                section.sequencePosition,
                                item.sequencePosition
                            );
                        }}
                    />
                </RadioWrapper>
            </CenteredRadioCell>
            <CenteredRadioCell>
                <RadioWrapper>
                    <StyledRadioGroup
                        name={`notApplicable-${section.sequencePosition}-${item.sequencePosition}`}
                        options={[{ id: true, display: '' }]}
                        value={item.notApplicable}
                        disabled={!editing}
                        onClick={() => {
                            itemOnClick(
                                'notApplicable',
                                section.sequencePosition,
                                item.sequencePosition
                            );
                        }}
                    />
                </RadioWrapper>
            </CenteredRadioCell>
        </>
    );
};
ChecklistItem.propTypes = {
    section: PropTypes.shape({
        name: PropTypes.string,
        sequencePosition: PropTypes.number,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                description: PropTypes.string,
                sequencePosition: PropTypes.number,
            })
        ),
    }),
    item: PropTypes.shape({
        description: PropTypes.string,
        sequencePosition: PropTypes.number,
        complete: PropTypes.bool,
        notApplicable: PropTypes.bool,
    }),
    editing: PropTypes.bool.isRequired,
    itemOnClick: PropTypes.func.isRequired,
};

function _editingAvailable(checklist, workOrderStatus, workOrder, accountId) {
    const isCheckedIn = isContractorAccount()
        ? !!workOrder?.checkInsV2?.find(
              checkIn =>
                  !checkIn.checkedOutAt && checkIn.checkedInUserId === accountId
          )
        : true;

    return (
        (hasRole(['motili_owner', 'motili_admin']) ||
            (isContractorAccount() && isCheckedIn)) &&
        (!checklist.meetsClientRequirement ||
            !checklist.meetsProjectRequirement) &&
        workOrderStatus === 'IN_PROCESS'
    );
}

export default AdminWorkOrderRequirementsChecklistViewModal;

const CenteredRadioCell = styled.td`
    padding: 0 16px;
    vertical-align: middle !important;
`;

const RadioWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;

    input[type='radio'] {
        margin: 0;
        display: block;
    }
`;

const StyledRadioGroup = styled(RadioGroup)`
    div.wrapper {
        & > div {
            & > div {
                border-color: ${Config.color.inputBorderGray};
                background-color: ${props => (props.disabled ? '#E6E6E6' : '')};
            }
        }
    }
`;
